import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, WifiOff, Database, AlertCircle } from 'lucide-react';

export type ConnectionErrorType = 'backend' | 'database' | 'network';

interface ConnectionErrorProps {
  type: ConnectionErrorType;
  onRetry?: () => void;
  autoRetryInterval?: number; // in milliseconds, default 60000 (1 minute)
}

export function ConnectionError({
  type,
  onRetry,
  autoRetryInterval = 60000
}: ConnectionErrorProps) {
  const [timeUntilRetry, setTimeUntilRetry] = useState(autoRetryInterval / 1000);
  const [isRetrying, setIsRetrying] = useState(false);

  // Auto-retry logic
  useEffect(() => {
    const retryTimer = setInterval(() => {
      setIsRetrying(true);
      onRetry?.();
      setTimeout(() => setIsRetrying(false), 2000);
    }, autoRetryInterval);

    return () => clearInterval(retryTimer);
  }, [autoRetryInterval, onRetry]);

  // Countdown timer
  useEffect(() => {
    const countdownTimer = setInterval(() => {
      setTimeUntilRetry((prev) => {
        if (prev <= 1) return autoRetryInterval / 1000;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [autoRetryInterval]);

  const handleManualRetry = () => {
    setIsRetrying(true);
    setTimeUntilRetry(autoRetryInterval / 1000);
    onRetry?.();
    setTimeout(() => setIsRetrying(false), 2000);
  };

  const config = {
    backend: {
      icon: WifiOff,
      title: 'Service Unavailable',
      message: 'We are having trouble connecting to our servers. This usually resolves quickly.',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-900'
    },
    database: {
      icon: Database,
      title: 'Data Service Unavailable',
      message: 'Our data service is temporarily unavailable. We are working to restore it.',
      color: 'from-blue-500 to-purple-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-900'
    },
    network: {
      icon: AlertCircle,
      title: 'Connection Lost',
      message: 'Please check your internet connection and try again.',
      color: 'from-gray-500 to-gray-700',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-900'
    }
  };

  const { icon: Icon, title, message, color, bgColor, borderColor, textColor } = config[type];
  const progress = ((autoRetryInterval / 1000 - timeUntilRetry) / (autoRetryInterval / 1000)) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
      >
        <motion.div
          className={`max-w-md w-full ${bgColor} border ${borderColor} rounded-2xl shadow-xl p-8`}
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          {/* Icon with animated pulse */}
          <motion.div
            className={`w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br ${color} flex items-center justify-center`}
            animate={{
              scale: [1, 1.1, 1],
              rotate: isRetrying ? 360 : 0
            }}
            transition={{
              scale: {
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut'
              },
              rotate: {
                duration: 0.6,
                ease: 'easeInOut'
              }
            }}
          >
            <Icon className="w-10 h-10 text-white" strokeWidth={2.5} />
          </motion.div>

          {/* Title */}
          <h2 className={`text-2xl font-bold text-center mb-3 ${textColor}`}>
            {title}
          </h2>

          {/* Message */}
          <p className={`text-center mb-6 ${textColor} opacity-80`}>
            {message}
          </p>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className={`${textColor} opacity-60`}>
                {isRetrying ? 'Retrying...' : 'Next retry in'}
              </span>
              <span className={`font-mono font-semibold ${textColor}`}>
                {isRetrying ? '' : `${timeUntilRetry}s`}
              </span>
            </div>
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className={`absolute inset-y-0 left-0 bg-gradient-to-r ${color} rounded-full`}
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Retry button */}
          <motion.button
            onClick={handleManualRetry}
            disabled={isRetrying}
            className={`w-full py-3 px-6 bg-gradient-to-r ${color} text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            whileHover={{ scale: isRetrying ? 1 : 1.02 }}
            whileTap={{ scale: isRetrying ? 1 : 0.98 }}
          >
            <RefreshCw
              className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`}
            />
            <span>{isRetrying ? 'Retrying...' : 'Retry Now'}</span>
          </motion.button>

          {/* Help text */}
          <p className={`text-center text-xs mt-4 ${textColor} opacity-60`}>
            Automatically retrying every minute
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
